import React, { useEffect, useMemo, useState, useRef } from 'react';
import { fetchBookings, addPayment, paymentReminder } from '../api/Booking';
import { Booking } from '../types/Booking';
import { Payment } from '../types/Payment';
import { User } from '../types/User';
import { fetchCustomerById } from '../api/Customer';
import { formatDate } from '../utils/date';

import Layout from '../components/Layout';
import SearchBar from '../components/SearchBar';
import CustomerInfoModal from '../components/Customer/CustomerInfoModal';
import AssignmentModal from '../components/Booking/AssignmentModal';
import PaymentHistoryModal from '../components/Booking/PaymentHistoryModal';
import AddPaymentModal from '../components/Booking/AddPaymentModal';
import QueryPickerModal from '../components/Query/QueryPickerModal';
import AddBookingModal from '../components/Booking/AddBookingModal';
import '../css/Pages.css';
import '../css/PackagesPage.css';

import * as FiIcons from 'react-icons/fi';
import * as TbIcons from "react-icons/tb";
const FiDownload = FiIcons.FiDownload as unknown as React.FC;
const FiShare2 = FiIcons.FiShare2 as unknown as React.FC;
const FaEye = FiIcons.FiEye as unknown as React.FC;
const FaPlus = FiIcons.FiPlus as unknown as React.FC;
const FaAssign = FiIcons.FiUserPlus as unknown as React.FC;
const TbSend = TbIcons.TbSend as unknown as React.FC;
const FiRefreshCw = FiIcons.FiRefreshCw as unknown as React.FC;

const BookingsPage = () => {
  const hasFetched = useRef(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(false);
  const [showQueryPicker, setShowQueryPicker] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState<any | null>(null);
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const data = await fetchBookings();
      setBookings(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    loadBookings();
  }, []);

  // Parent/children grouping helpers
  const getParentId = (b: any): number | null => {
    return b.parent_id || b.parentId || b.parent_booking_id || b.parentBookingId || null;
  };
  const childrenByParentId = useMemo(() => {
    const map = new Map<number, any[]>();
    (bookings as any[]).forEach((b: any) => {
      const pid = getParentId(b);
      if (pid) {
        if (!map.has(pid)) map.set(pid, []);
        map.get(pid)!.push(b);
      }
    });
    return map;
  }, [bookings]);

  const parentBookings = useMemo(() => (bookings as any[]).filter((b: any) => !getParentId(b)), [bookings]);

  const filtered = parentBookings.filter(b => {
    const matchesStatus = statusFilter ? (b.status || '').toLowerCase() === statusFilter.toLowerCase() : true;
    const matchesAssignee = assigneeFilter ? b.assigned_to?.toString() === assigneeFilter : true;
    const q = searchTerm.trim().toLowerCase();
    const matchesSearch = !q || [
      b.package?.title || '',
      b.customer?.name || '',
      String(b.id),
      String(b.query_id || ''),
      b.user?.name || ''
    ].some(s => s.toLowerCase().includes(q));
    return matchesStatus && matchesAssignee && matchesSearch;
  });


  const openAssignmentModal = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowAssignmentModal(true);
  };

  const handleAssign = async (bookingId: number | string, userId: number) => {
    setBookings(prev =>
      prev.map(booking =>
        booking.id === bookingId ? { ...booking, assigned_to: userId } : booking
      )
    );
  };

  const openPaymentHistoryModal = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowPaymentHistory(true);
  };

  const openAddPaymentModal = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowAddPaymentModal(true);
  };

  const handleAddPayment = async (paymentData: {
    amount: number;
    method: string;
    transactionId: string;
    notes?: string;
  }) => {
    if (!selectedBooking) return;
    await addPayment(selectedBooking.id, paymentData)
      .then(() => alert('Payment added successfully!'))
      .catch(() => alert('Error adding payment.'));
    fetchBookings().then(setBookings);
  };

  const handleSendPaymentReminder = async (bookingId: number) => {
    await paymentReminder(bookingId)
      .then(() => alert('Payment reminder sent!'))
      .catch(() => alert('Error sending payment reminder.'));
  };

  const handleDownloadVouchers = (booking: Booking) => {
    const url = (booking as any).pdf_url || (booking as any).voucher_url || '';
    if (url) {
      window.open(url, '_blank', 'noopener');
    } else {
      alert('No vouchers available for download.');
    }
  };

  const handleShareVouchers = async (booking: Booking) => {
    const url = (booking as any).pdf_url || (booking as any).voucher_url || '';
    if (!url) {
      alert('No vouchers available to share.');
      return;
    }
    try {
      if (navigator.share) {
        await navigator.share({ title: booking.package?.title || 'Vouchers', url });
      } else if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        alert('Voucher link copied to clipboard');
      } else {
        // Fallback: open in new tab
        window.open(url, '_blank', 'noopener');
      }
    } catch (e) {
      // ignored
    }
  };

  const uniqueAssignees = Array.from(
    new Map<number, User>(
      bookings
        .map(b => b.user)
        .filter((u): u is User => !!u && typeof u.id === 'number')
        .map(u => [u.id, u] as [number, User])
    ).values()
  );

  // Component types to summarize per booking
  const COMPONENTS = [
    { key: 'flight', label: 'Flight' },
    { key: 'hotel', label: 'Hotel' },
    { key: 'transfer', label: 'Transfers' },
    { key: 'activity', label: 'Activity' },
    { key: 'insurance', label: 'Insurance' },
    { key: 'visa', label: 'VISA' },
  ];

  const getTypeString = (k: any): string => (
    (k?.type || k?.category || k?.service || k?.package?.title || '').toString().toLowerCase()
  );
  const matchesComponent = (k: any, compKey: string): boolean => {
    const s = getTypeString(k);
    if (!s) return false;
    if (compKey === 'activity') return s.includes('activity');
    if (compKey === 'transfer') return s.includes('transfer');
    if (compKey === 'visa') return s.includes('visa');
    return s.includes(compKey);
  };
  const computeComponentState = (kids: any[], compKey: string): 'Not Needed' | 'Needed' | 'Booked' => {
    const k = kids.find(kk => matchesComponent(kk, compKey));
    if (!k) return 'Not Needed';
    const status = String(k.status || k.booking_status || k.state || '').toLowerCase();
    if (status.includes('book') || status.includes('confirm')) return 'Booked';
    return 'Needed';
  };

  const openCustomerModal = async (customerId?: number) => {
    if (!customerId) return;
    try {
      const customer = await fetchCustomerById(customerId);
      setSelectedCustomer(customer);
    } catch (err) {
    }
  };

  return (
    <Layout>
      <div className="packages-header">
        <h2>Bookings</h2>
        <div className="packages-actions">
          <button className="btn" onClick={() => loadBookings()} disabled={loading} title="Reload bookings">
            <FiRefreshCw /> {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button className="btn" onClick={() => setShowQueryPicker(true)} title="Create booking from a query">
            <FaPlus /> Add Booking
          </button>
        </div>
      </div>

      <div className="packages-filters">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search by package, customer, ID"
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="filter-select" aria-label="Status">
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="CONFIRMED">Confirmed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>

        <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)} className="filter-select" aria-label="Assignee">
          <option value="">All Sales</option>
          {uniqueAssignees.map(user => (
            <option key={user.id} value={user.id}>{user.name}</option>
          ))}
        </select>

        <button className="clear-btn" onClick={() => {
          setStatusFilter('');
          setAssigneeFilter('');
          setSearchTerm('');
          loadBookings();
        }}>Clear</button>
      </div>

      <div className="packages-table-wrapper">
        <table className="packages-table bookings-table">
          <thead>
            <tr>
              <th>Details</th>
              <th>Payment</th>
              <th>Components</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(b => {
              const paid = (b.payments || []).reduce((sum: number, p: Payment) => sum + (Number(p.amount) || 0), 0);
              const kids = childrenByParentId.get(b.id) || [];

              return (
                <tr key={b.id}>
                  <td className="col-query">
                    {(() => {
                      const start = b.travel_start_date ? new Date(String(b.travel_start_date)) : null;
                      const end = b.travel_end_date ? new Date(String(b.travel_end_date)) : null;
                      const nights = start && end ? Math.max(0, Math.round((end.getTime() - start.getTime()) / (1000*60*60*24))) : undefined;
                      const shortRange = start && end
                        ? `${start.getDate()}–${end.getDate()} ${end.toLocaleString('default', { month: 'short' })}`
                        : '';
                      const title = b.package?.title || b.query?.location || '—';
                      return (
                        <>
                          <div className="q-col-title">{title}{shortRange ? ` (${shortRange})` : ''}{nights !== undefined ? ` · ${nights}N` : ''}</div>
                          <div className="q-col-line small">Booking #{b.id}{b.query_id ? ` · Query #${b.query_id}` : ''} · Created {formatDate(b.created_at)}</div>
                          <div className="q-col-line">
                            <span className={`status-dot ${String(b.status || '').toLowerCase()}`} />
                            <span className="status-text">{b.status || '—'}</span>
                            <span className="sep">·</span>
                            <span className="label">Assignee:</span> {b.user?.name || 'N/A'}
                          </div>
                          <div className="q-col-line">
                            <span className="label">Customer:</span> 
                            <span
                              className="clickable-name"
                              onClick={() => openCustomerModal(b.customer?.id)}
                            >
                              {b.customer?.name || 'Unknown'}
                            </span>
                            {b.customer?.email && (
                              <>
                                <span className="sep">·</span>
                                <a href={`mailto:${b.customer.email}`}>{b.customer.email}</a>
                              </>
                            )}
                            {b.customer?.phoneNumber && (
                              <>
                                <span className="sep">·</span>
                                <a href={`tel:${b.customer.phoneNumber}`}>{b.customer.phoneNumber}</a>
                              </>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </td>
                  <td className="col-payment">
                    <div className="pay-line">₹{paid.toLocaleString('en-IN')} / ₹{(b.total_price || 0).toLocaleString('en-IN')}</div>
                    {(() => {
                      const pct = (b.total_price || 0) > 0 ? Math.min(100, Math.round((paid / (b.total_price || 1)) * 100)) : 0;
                      const cls = pct <= 10 ? 'pay-red' : pct <= 40 ? 'pay-orange' : pct <= 70 ? 'pay-amber' : pct < 100 ? 'pay-sky' : 'pay-green';
                      return (
                        <div className="pay-progress">
                          <div className={`bar ${cls}`} style={{ width: `${pct}%` }} />
                          <div className="pct">{pct}%</div>
                        </div>
                      );
                    })()}
                    <div className="pay-actions">
                      <button className="btn-secondary" onClick={() => openPaymentHistoryModal(b)}><FaEye /> History</button>
                      <button className="btn-secondary" onClick={() => openAddPaymentModal(b)}>+ Add</button>
                    </div>
                  </td>
                  <td className="col-components">
                    <div className="components-list">
                      {COMPONENTS.map((c) => {
                        const state = computeComponentState(kids as any[], c.key);
                        const cls = state === 'Booked' ? 'state-booked' : state === 'Needed' ? 'state-needed' : 'state-notneeded';
                        return (
                          <div key={c.key} className="comp-line">
                            <span className="comp-label">{c.label}</span>
                            <span className={`comp-state ${cls}`}>— {state}</span>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                  <td className="col-actions">
                    <div className="action-buttons two-per-row">
                      <button className="btn-secondary" onClick={() => handleSendPaymentReminder(b.id)} title="Send Reminder"><TbSend /> Send Reminder</button>
                      <button className="btn-secondary" onClick={() => handleShareVouchers(b)} title="Share vouchers"><FiShare2 /> Share Vouchers</button>
                      <button className="btn-secondary" onClick={() => handleDownloadVouchers(b)} title="Download vouchers"><FiDownload /> Download Vouchers</button>
                      <button className="btn-secondary" onClick={() => openAssignmentModal(b)} title="Reassign booking"><FaAssign /> Reassign</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="empty-state">No bookings found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedCustomer && (
        <CustomerInfoModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      {showAssignmentModal && selectedBooking && (
        <AssignmentModal
          bookingId={selectedBooking.id}
          users={uniqueAssignees}
          onClose={() => setShowAssignmentModal(false)}
          onAssign={handleAssign}
        />
      )}

      {showPaymentHistory && selectedBooking && (
        <PaymentHistoryModal
          isOpen={true}
          onClose={() => {
            setSelectedBooking(null);
            setShowAssignmentModal(false);
          }}
          payments={selectedBooking.payments || []}
          total={selectedBooking.total_price || 0}
        />
      )}

      {showAddPaymentModal && selectedBooking && (
        <AddPaymentModal
          isOpen={true}
          onClose={() => {
            setSelectedBooking(null);
            setShowAddPaymentModal(false);
          }}
          onSubmit={handleAddPayment}
          defaultAmount={selectedBooking.total_price}
        />
      )}

      {showQueryPicker && (
        <QueryPickerModal
          onClose={() => setShowQueryPicker(false)}
          onSelect={(q) => {
            setShowQueryPicker(false);
            setSelectedQuery(q);
            setShowAddBookingModal(true);
          }}
        />
      )}

      {showAddBookingModal && selectedQuery && (
        <AddBookingModal
          query={selectedQuery}
          onClose={() => setShowAddBookingModal(false)}
          onCreated={() => {
            setShowAddBookingModal(false);
            loadBookings();
          }}
        />
      )}
    </Layout>
  );
};

export default BookingsPage;
